import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { z } from "zod"
import { GuidedForm } from "@/components/forms/guided-form"
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { STEPS } from "@/constants/steps"
import {
  getInventIframeToken,
  type InventGuidedFormLifecycleResult,
  postInventGuidedFormLifecycle,
} from "@/lib/invent-iframe-bridge"
import { getInventPortalOrigin } from "@/lib/invent-widget-runtime"
import { trpc } from "@/lib/trpc"

type StepId = (typeof STEPS)[number]["id"]

const searchSchema = z.object({
  currentStep: z
    .enum([...STEPS.map((step) => step.id)] as [StepId, ...StepId[]])
    .optional()
    .catch("intro"),
})

export const Route = createFileRoute("/integrations/invent/embed/guided-form")({
  component: EmbeddedGuidedForm,
  validateSearch: (search) => searchSchema.parse(search),
})

type ConnectionState = "connecting" | "connected" | "error"

function EmbeddedGuidedForm() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting")
  const [parentOrigin, setParentOrigin] = useState("")
  const [selectedEstateId, setSelectedEstateId] = useState("")
  const previousSelectedEstateId = useRef<string | undefined>(undefined)
  const lifecycleResultSent = useRef(false)

  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = trpc.users.me.useQuery(undefined, { enabled: connectionState === "connected" })

  const isProfessional = user?.userType === "professional"
  const {
    data: estates = [],
    isLoading: areEstatesLoading,
    isError: isEstatesError,
  } = trpc.estates.list.useQuery(undefined, {
    enabled: connectionState === "connected" && isProfessional,
  })

  const eligibleEstates = useMemo(() => estates.filter((estate) => !estate.journalOnly), [estates])
  const effectiveSelectedEstateId = eligibleEstates.some((estate) => estate.id === selectedEstateId)
    ? selectedEstateId
    : (eligibleEstates[0]?.id ?? "")

  const resetToIntro = useCallback(() => {
    navigate({
      to: "/integrations/invent/embed/guided-form",
      search: (previous) => ({ ...previous, currentStep: "intro" }),
      replace: true,
    })
  }, [navigate])

  useEffect(() => {
    let active = true

    getInventPortalOrigin()
      .then((configuredParentOrigin) => {
        if (active) setParentOrigin(configuredParentOrigin)
        return getInventIframeToken({ parentOrigin: configuredParentOrigin })
      })
      .then(
        () => {
          if (active) setConnectionState("connected")
        },
        () => {
          if (active) setConnectionState("error")
        },
      )

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!isProfessional) {
      setSelectedEstateId("")
      previousSelectedEstateId.current = undefined
      return
    }

    const previousEstateId = previousSelectedEstateId.current
    if (
      previousEstateId &&
      effectiveSelectedEstateId &&
      previousEstateId !== effectiveSelectedEstateId
    ) {
      resetToIntro()
    }

    previousSelectedEstateId.current = effectiveSelectedEstateId || undefined
    if (selectedEstateId !== effectiveSelectedEstateId) {
      setSelectedEstateId(effectiveSelectedEstateId)
    }
  }, [effectiveSelectedEstateId, isProfessional, resetToIntro, selectedEstateId])

  const lifecycleResult = useMemo<InventGuidedFormLifecycleResult | undefined>(() => {
    if (connectionState === "error") return "error"
    if (connectionState !== "connected" || isUserLoading) return undefined
    if (isUserError || !user) return "error"

    if (user.userType === "professional") {
      if (areEstatesLoading) return undefined
      if (isEstatesError) return "error"
      if (!effectiveSelectedEstateId) return "no-data"
    } else if (!user.estateId) {
      return "no-data"
    }

    return "ready"
  }, [
    areEstatesLoading,
    connectionState,
    effectiveSelectedEstateId,
    isEstatesError,
    isUserError,
    isUserLoading,
    user,
  ])

  useEffect(() => {
    if (!lifecycleResult || lifecycleResultSent.current) return

    const sent = postInventGuidedFormLifecycle({
      parentOrigin,
      result: lifecycleResult,
    })
    if (sent) lifecycleResultSent.current = true
  }, [lifecycleResult, parentOrigin])

  if (connectionState === "connecting") {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <output>Connecting to INVENT...</output>
      </main>
    )
  }

  if (connectionState === "error") {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <p role="alert">Unable to connect to INVENT. Reload this page from the INVENT portal.</p>
      </main>
    )
  }

  if (isUserLoading || (isProfessional && areEstatesLoading)) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <output>Loading guided form...</output>
      </main>
    )
  }

  if (isUserError || !user) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <p role="alert">Unable to load your Legacy Keeper profile.</p>
      </main>
    )
  }

  if (isProfessional && isEstatesError) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <p role="alert">Unable to load clients.</p>
      </main>
    )
  }

  const estateId = isProfessional ? effectiveSelectedEstateId : user.estateId
  if (!estateId) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <p>
          {isProfessional
            ? "No clients are available for this guided form."
            : "No estate is available for this guided form."}
        </p>
      </main>
    )
  }

  const handleClientChange = (nextEstateId: string) => {
    previousSelectedEstateId.current = nextEstateId
    setSelectedEstateId(nextEstateId)
    resetToIntro()
  }

  return (
    <main className="min-h-screen p-4 sm:p-6">
      {isProfessional && (
        <div className="mb-4 space-y-2">
          <label htmlFor="invent-client-selector" className="block text-sm font-medium">
            Client
          </label>
          <SelectRoot value={effectiveSelectedEstateId} onValueChange={handleClientChange}>
            <SelectTrigger id="invent-client-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {eligibleEstates.map((estate) => (
                <SelectItem key={estate.id} value={estate.id}>
                  {estate.estateName}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        </div>
      )}
      <GuidedForm
        currentStep={search.currentStep ?? "intro"}
        estateIdOverride={estateId}
        userTypeOverride={user.userType}
      />
    </main>
  )
}
